import { gql } from '@apollo/client';

export const CREATE_MODEL_PORTFOLIO = gql`
  mutation createModelPortfolio($input: CreateModelPortfolioInput!) {
    createModelPortfolio(input: $input) {
      modelPortfolio {
        id
        name
        modelType
        state
        settlementDays
        suggestedMinScore
        suggestedMaxScore
        suggestable
        theme {
          core
          description
          iconKey
          id
          key
          name
          state
          translatedDescription {
            en
            fr
          }
          translatedName {
            en
            fr
          }
          translatedShortDescription {
            en
            fr
          }
        }
        portfolioTemplate {
          assetClassBranches {
            assetClass {
              description
              id
              includeCash
              key
              name
              state
              translatedDescription {
                en
                fr
              }
              translatedName {
                en
                fr
              }
            }
            id
            targetPercentage
          }
          createdAt
          id
          name
          state
          translatedName {
            en
            fr
          }
          translatedTemplateDescription {
            en
            fr
          }
          updatedAt
        }
        priceHistoryUrl
        portfolioDisclaimer
        translatedPortfolioDescription {
          en
          fr
        }
        translatedPortfolioDisclaimer {
          en
          fr
        }
        url
        translatedThemeName {
          en
          fr
        }
        translatedName {
          en
          fr
        }
        translatedLiquidity {
          en
          fr
        }
        translatedInvestmentObjective {
          en
          fr
        }
        tradeable
        targetAllocations {
          max
          min
          secondaryAssetClass {
            id
            key
            name
          }
        }
        forecastedStandardDeviation
        forecastedRateOfReturn
        children {
          id
          isCashEquivalent
          percentage
          taxRank
          financialProduct {
            id
            name
            primaryAssetClass {
              id
              key
              name
              translatedName {
                en
                fr
              }
              description
              state
            }
          }
        }
      }
    }
  }
`;

export const UPDATE_MODEL_PORTFOLIO = gql`
  mutation updateModelPortfolio($input: UpdateModelPortfolioInput!) {
    updateModelPortfolio(input: $input) {
      modelPortfolio {
        id
        name
        modelType
        state
        settlementDays
        suggestedMinScore
        suggestedMaxScore
        suggestable
        theme {
          core
          description
          iconKey
          id
          key
          name
          state
          translatedDescription {
            en
            fr
          }
          translatedName {
            en
            fr
          }
          translatedShortDescription {
            en
            fr
          }
        }
        portfolioTemplate {
          assetClassBranches {
            assetClass {
              description
              id
              includeCash
              key
              name
              state
              translatedDescription {
                en
                fr
              }
              translatedName {
                en
                fr
              }
            }
            id
            targetPercentage
          }
          createdAt
          id
          name
          state
          translatedName {
            en
            fr
          }
          translatedTemplateDescription {
            en
            fr
          }
          updatedAt
        }
        priceHistoryUrl
        portfolioDisclaimer
        translatedPortfolioDescription {
          en
          fr
        }
        translatedPortfolioDisclaimer {
          en
          fr
        }
        url
        translatedThemeName {
          en
          fr
        }
        translatedName {
          en
          fr
        }
        translatedLiquidity {
          en
          fr
        }
        translatedInvestmentObjective {
          en
          fr
        }
        tradeable
        targetAllocations {
          max
          min
          secondaryAssetClass {
            id
            key
            name
          }
        }
        forecastedStandardDeviation
        forecastedRateOfReturn
        children {
          id
          isCashEquivalent
          percentage
          taxRank
          financialProduct {
            id
            name
            primaryAssetClass {
              id
              key
              name
              translatedName {
                en
                fr
              }
              description
              state
            }
          }
        }
      }
    }
  }
`;

export const TRANSITION_MODEL_PORTFOLIO = gql`
  mutation transitionModelPortfolio($input: TransitionModelPortfolioInput!) {
    transitionModelPortfolio(input: $input) {
      modelPortfolio {
        id
      }
    }
  }
`;

export const FETCH_MODEL_PORTFOLIO = gql`
  query fetchModelPortfolio($input: FetchModelPortfolioInput!) {
    fetchModelPortfolio(input: $input) {
      modelPortfolio {
        id
        assignable
        translatedName {
          en
          fr
        }
        translatedInvestmentObjective {
          en
          fr
        }
        translatedLiquidity {
          en
          fr
        }
        translatedPortfolioDescription {
          en
          fr
        }
        translatedPortfolioDisclaimer {
          en
          fr
        }
        theme {
          id
          name
        }
        children {
          id
          percentage
          isCashEquivalent
          taxRank
          financialProduct {
            id
            isCash
            ticker
            translatedName {
              en
            }
            primaryAssetClass{
              id
              translatedName {
                en
              }
            }
            secondaryAssetClass{
              id
              translatedName {
                en
              }
            }
            taxRank
          }
        }
        exchange
        forecastedRateOfReturn
        forecastedStandardDeviation
        lowThresholdScore
        reasonForUpdate
        riskProfileName {
          en
          fr
        }
        riskReturnLevel
        state
        suggestable
        zeroPercentCash
        modelType
        strategy
        suggestedMaxScore
        suggestedMinScore
        url
        strategy
        targetAllocations {
          min
          max
          secondaryAssetClass{
            id
            translatedName {
              en
              fr
            }
          }
        }
      }
    }
  }
`;

export const FETCH_MODEL_PORTFOLIOS = gql`
  query fetchModelPortfolios($input: FetchModelPortfoliosInput!) {
    fetchModelPortfolios(input: $input) {
      modelPortfolios {
        id
        name
        state
      }
    }
  }
`;

export const WORKFLOW_COMPLETION = (permissions: string[]) => `#graphql
  workflowCompletion {
    id
    objectId
    objectType
    context
    workflow {
      id
      complex
      settings {
        displayCloseButton
      }
      name { en fr }
      steps {
        id
        name { en fr }
        subSteps { id type }
      }
    }
    organization {
      id
    }
    currentStep {
      id
      name { en fr }
      subSteps { id type }
    }
    currentSubStep {
      id
      options
      type
      canGoBack
      skippable
      rolesCompleteableBy { id }
    }
  }
`;

export const NEXT_STEP = (permissions: string[]) => gql`
  mutation nextSubStep($input: NextSubStepInput!) {
    nextSubStep(input: $input) {
      ${WORKFLOW_COMPLETION(permissions)}
    }
  }
`;

export const PREV_STEP = (permissions: string[]) => gql`
  mutation previousSubStep($input: PreviousSubStepInput!) {
    previousSubStep(input: $input) {
      ${WORKFLOW_COMPLETION(permissions)}
    }
  }
`;

export const GO_TO_STEP = (permissions: string[]) => gql`
  mutation goToSubStep($input: GoToSubStepInput!) {
    goToSubStep(input: $input) {
      ${WORKFLOW_COMPLETION(permissions)}
    }
  }
`;

export const UPDATE_WORKFLOW_COMPLETION = (permissions: string[]) => gql`
  mutation updateWorkflowCompletion($input: UpdateWorkflowCompletionInput!) {
    updateWorkflowCompletion(input: $input) {
      ${WORKFLOW_COMPLETION(permissions)}
    }
  }
`;

export const CREATE_WORKFLOW_COMPLETION = (permissions: string[]) => gql`
  mutation createWorkflowCompletion($input: CreateWorkflowCompletionInput!) {
    createWorkflowCompletion(input: $input) {
      ${WORKFLOW_COMPLETION(permissions)}
    }
  }
`;
